// Setup › General (`/c/{slug}/setup/general`; design spec §9 "General", coverage matrix §2.1):
// the company form on the settings template. One `PATCH /v1/companies/{co}` saves it all:
// - Company: name, reporting currency, time zone, week start, fiscal year start (row 4);
// - Friday report (`#friday-report`): generation time, auto-send, extra recipients, and who of
//   the default recipients (owners, admins, client viewers) gets it (row 6, opt-outs);
// - Cost rates (row 5): each person's hourly rate; without one their time posts no cost;
// - Security (HARDEN-mfa): "Require two-step sign-in for owners and admins"
//   (`settings.security`). Only an owner changes it, and turning it on needs an owner who uses
//   it (the API answers 403 otherwise); others see it disabled with why. The patch carries
//   `security` only when the owner changed it, so anyone else's save never touches it.
// Two calls on load: the company and the first page of people (for opt-outs and rates). Rows
// already stored for people not loaded yet are kept as they are. Every write spreads the stored
// settings, so keys this form does not show (`flags`, rate `kinds`) survive a save.
// - Sharing with Founder OS (FOUNDER-OS, an operating company): what crosses to a founder's
//   portfolio holding it (`settings.founder_sharing`). Only an owner changes it; others see it
//   read-only. It starts from what the API applies now (`companies.founderSharing`: the setting,
//   else the default by how a portfolio holds the company), so editing one kind never widens the
//   others; the patch carries it only when changed.
// SETUP-UX: three `SettingsGroup`s of label-left / control-right rows; the SaveDock stays.

import { ApiError } from "@companyos/sdk";
import {
  FieldError,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  SaveDock,
  SaveIndicator,
  SettingsGroup,
  SettingsRow,
  Switch,
  Textarea,
} from "@companyos/ui";
import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyDetail, FounderSharing, FounderSharingView, Person } from "../../api/types";
import type { CompanyAccess } from "../company";
import { fractionDigits } from "../format";
import { companyQuery, keys, peopleQuery } from "../queries";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { useSession } from "../use-feature";
import { CompanyExportGroup } from "./company-export";
import { SetupPage } from "./setup-page";

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD"] as const;
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
/** Roles that get the Friday report by default (S1-14 decision). */
const REPORT_ROLES = ["owner", "admin", "client_viewer"];

const TIME_ZONES: string[] = (() => {
  try {
    return (
      (Intl as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ??
      []
    );
  } catch {
    return [];
  }
})();

function validTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en", { timeZone: tz });
    return tz.length > 0;
  } catch {
    return false;
  }
}

type Rates = CompanyDetail["settings"]["human_time_rates"];

type FormState = {
  name: string;
  currency: string;
  timezone: string;
  weekStartDay: number;
  fiscalMonth: number;
  generateAt: string;
  autoSend: boolean;
  recipients: string;
  optedOut: string[];
  /** Hourly rate per membership, as typed (major units); "" = no rate. */
  rates: Record<string, string>;
  /** HARDEN-mfa: owners and admins must use two-step sign-in. */
  requireMfa: boolean;
  /**
   * FOUNDER-OS: what crosses to a Founder OS holding the company (the owners'), as the API
   * applies it now (`companies.founderSharing` `effective`); null when it could not be read.
   */
  sharing: FounderSharing | null;
  /** HELPERS: DEs are named by role (default) or by an optional person name before it. */
  deNames: "role" | "person";
};

/** FOUNDER-OS: where the effective sharing comes from, in a few words (the (i)'s text). */
const SHARING_SOURCE: Record<FounderSharingView["source"], string> = {
  setting: "",
  owned_default: "Default for an owned company.",
  client_default: "Default for a client company.",
};

export const founderSharingQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: ["company", co, "founder-sharing"] as const,
    queryFn: () => api.client.companies.founderSharing({ params: { co } }),
    retry: false,
  });

/** The on/off kinds of the sharing setting, in the order the section lists them. */
const SHARING_TOGGLES = [
  ["goals", "Priorities"],
  ["issues", "Issues"],
  ["escalation", "Escalations"],
  ["decisions", "Approvals"],
  ["headcount", "Headcount"],
  ["meetings", "Meetings"],
] as const;

function stateOf(d: CompanyDetail, sharing: FounderSharingView | null = null): FormState {
  const digits = fractionDigits(d.reporting_currency);
  const rates: Record<string, string> = {};
  for (const r of d.settings.human_time_rates ?? [])
    if (!(r.membership_id in rates))
      rates[r.membership_id] = (r.hourly_minor / 10 ** digits).toFixed(digits);
  return {
    name: d.name,
    currency: d.reporting_currency,
    timezone: d.calendar_config.timezone,
    weekStartDay: d.calendar_config.weekStartDay,
    fiscalMonth: d.calendar_config.fiscalYearStartMonth,
    generateAt: d.settings.friday_report?.generate_at ?? "15:00",
    autoSend: d.settings.friday_report?.auto_send ?? false,
    recipients: (d.settings.friday_report?.recipients_extra ?? []).join("\n"),
    optedOut: [...(d.settings.friday_report?.opted_out_membership_ids ?? [])],
    rates,
    requireMfa: d.settings.security?.require_two_factor_for_admins === true,
    // Never a default the API does not apply: an edit must not widen the other kinds.
    sharing: d.settings.founder_sharing ?? sharing?.effective ?? null,
    deNames: d.settings.workforce?.de_names ?? "role",
  };
}

const same = (a: FormState, b: FormState) => JSON.stringify(a) === JSON.stringify(b);

/**
 * REPORTS-UI: the opt-out list to save: the stored one now, with the manager's own changes on
 * this form (ids added or removed since it loaded) applied; everyone else's entries kept.
 */
export function mergeOptOuts(
  stored: readonly string[],
  loaded: readonly string[],
  edited: readonly string[],
): string[] {
  const added = edited.filter((id) => !loaded.includes(id));
  const removed = new Set(loaded.filter((id) => !edited.includes(id)));
  return [...new Set([...stored.filter((id) => !removed.has(id)), ...added])];
}

export function GeneralSettings({ api, company }: { api: Api; company: CompanyAccess }) {
  const detail = useQuery(companyQuery(api, company.id));
  const operating = company.kind !== "portfolio";
  // FOUNDER-OS: the sharing as the API applies it (the setting, else its default).
  const sharing = useQuery({ ...founderSharingQuery(api, company.id), enabled: operating });
  return (
    <SetupPage title="General">
      {detail.isPending || (operating && sharing.isPending) ? (
        <Loading label="Loading settings" rows={6} />
      ) : detail.isError ? (
        <LoadError what="Settings" error={detail.error} />
      ) : (
        // Initialised once per company: a background refetch never overwrites what is typed.
        <GeneralForm
          key={detail.data.id}
          api={api}
          detail={detail.data}
          owner={company.roles.includes("owner")}
          operating={operating}
          sharing={sharing.data ?? null}
        />
      )}
      {/* EXPORT-CORE: owners download the Company Archive (company-export.tsx). */}
      {company.roles.includes("owner") ? <CompanyExportGroup api={api} company={company} /> : null}
    </SetupPage>
  );
}

function GeneralForm({
  api,
  detail,
  owner,
  operating = true,
  sharing: sharingView = null,
}: {
  api: Api;
  detail: CompanyDetail;
  /** HARDEN-mfa: only an owner changes the two-step requirement (FOUNDER-OS: and sharing). */
  owner: boolean;
  /** FOUNDER-OS: an operating company (not Founder OS) has the sharing section. */
  operating?: boolean;
  /** FOUNDER-OS: `companies.founderSharing` (null: not read). */
  sharing?: FounderSharingView | null;
}) {
  const { me } = useSession();
  const qc = useQueryClient();
  const [view, setView] = useState(sharingView);
  const [base, setBase] = useState<FormState>(() => stateOf(detail, view));
  const [form, setForm] = useState<FormState>(base);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const people = useInfiniteQuery(peopleQuery(api, detail.id));
  const persons = useMemo(
    () => (people.data?.pages.flatMap((p) => p.data) ?? []).filter((p) => p.kind === "person"),
    [people.data],
  );
  const digits = fractionDigits(form.currency);

  const recipients = form.recipients
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const badRecipient = recipients.find((r) => !EMAIL.test(r));
  const badZone = !validTimeZone(form.timezone.trim());
  const badRate = Object.entries(form.rates).find(
    ([, v]) => v.trim() !== "" && !(Number(v) >= 0 && Number.isFinite(Number(v))),
  )?.[0];
  const invalid = !form.name.trim() || !!badRecipient || badZone || !!badRate;
  const dirty = !same(form, base);

  const save = useMutation({
    mutationFn: async () => {
      // Rates: the typed ones replace their person's first entry (keeping its `kinds`);
      // stored rows of people this page has not loaded stay as they were.
      const loaded = new Set(persons.map((p) => p.id));
      const stored = detail.settings.human_time_rates ?? [];
      const rates: NonNullable<Rates> = [];
      const done = new Set<string>();
      for (const r of stored) {
        if (!loaded.has(r.membership_id) && !(r.membership_id in form.rates)) {
          rates.push(r);
          continue;
        }
        if (done.has(r.membership_id)) {
          rates.push(r);
          continue;
        }
        done.add(r.membership_id);
        const typed = form.rates[r.membership_id]?.trim() ?? "";
        if (typed !== "")
          rates.push({ ...r, hourly_minor: Math.round(Number(typed) * 10 ** digits) });
      }
      for (const [id, v] of Object.entries(form.rates))
        if (!done.has(id) && v.trim() !== "")
          rates.push({ membership_id: id, hourly_minor: Math.round(Number(v) * 10 ** digits) });
      // REPORTS-UI: members opt themselves out while this form is open. Read the stored list,
      // apply only what the manager changed here, and send it with that list as the expectation
      // (the API refuses a list that moved since with `conflict`: read again once and retry).
      const write = async (retry: boolean): Promise<Omit<CompanyDetail, "wallet_view">> => {
        const fresh = await api.client.companies.get({ params: { co: detail.id } });
        const now = fresh.settings.friday_report?.opted_out_membership_ids ?? [];
        try {
          return await update(mergeOptOuts(now, base.optedOut, form.optedOut), now);
        } catch (e) {
          if (retry && e instanceof ApiError && e.code === "conflict") return write(false);
          throw e;
        }
      };
      const update = (optedOut: string[], expected: string[]) =>
        api.client.companies.update({
          params: { co: detail.id },
          body: {
            opted_out_expected: expected,
            name: form.name.trim(),
            reporting_currency: form.currency as CompanyDetail["reporting_currency"],
            calendar_config: {
              ...detail.calendar_config,
              timezone: form.timezone.trim(),
              weekStartDay: form.weekStartDay,
              fiscalYearStartMonth: form.fiscalMonth,
            },
            settings: {
              ...detail.settings,
              friday_report: {
                ...detail.settings.friday_report,
                generate_at: form.generateAt,
                auto_send: form.autoSend,
                recipients_extra: recipients,
                opted_out_membership_ids: optedOut,
              },
              human_time_rates: rates,
              ...(form.deNames !== base.deNames
                ? { workforce: { ...detail.settings.workforce, de_names: form.deNames } }
                : {}),
              // HARDEN-mfa: only when the owner changed it (anything else keeps the stored one).
              ...(form.requireMfa !== base.requireMfa
                ? {
                    security: {
                      ...detail.settings.security,
                      require_two_factor_for_admins: form.requireMfa,
                    },
                  }
                : {}),
              // FOUNDER-OS: only when the owner changed it (omitted, the stored one is kept).
              ...(form.sharing && JSON.stringify(form.sharing) !== JSON.stringify(base.sharing)
                ? { founder_sharing: form.sharing }
                : {}),
            },
          },
        });
      return write(true);
    },
    onSuccess: (updated) => {
      // The update answers the company without the reader's `wallet_view`: keep the read one.
      const company: CompanyDetail = { ...updated, wallet_view: detail.wallet_view };
      qc.setQueryData(keys.company(detail.id), company);
      // `/v1/me` `mfa.required_company_ids` follows the requirement.
      if (stateOf(company).requireMfa !== base.requireMfa)
        void qc.invalidateQueries({ queryKey: ["me"] });
      // HELPERS: the DE lists and profiles carry person names only under "person" (the API masks
      // them otherwise), so a switch re-reads them (Codex).
      if (stateOf(company).deNames !== base.deNames) {
        void qc.invalidateQueries({ queryKey: keys.digitalEmployees(detail.id) });
        void qc.invalidateQueries({ queryKey: ["company", detail.id, "de"] });
      }
      // FOUNDER-OS: a stored setting is now the effective sharing.
      const stored = company.settings.founder_sharing;
      const nextView: FounderSharingView | null =
        stored && view ? { ...view, effective: stored, source: "setting" } : view;
      if (stored) {
        setView(nextView);
        void qc.invalidateQueries({ queryKey: founderSharingQuery(api, detail.id).queryKey });
      }
      const next = stateOf(company, nextView);
      setBase(next);
      setForm(next);
      setSavedAt(Date.now());
    },
  });

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const onSave = () => {
    if (!invalid) save.mutate();
  };
  const share = form.sharing;
  const reportPeople = persons.filter(
    (p) => p.status === "active" && p.roles.some((r) => REPORT_ROLES.includes(r)),
  );

  return (
    <>
      <form
        aria-label="Company settings"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSave();
        }}
      >
        <SettingsGroup id="company" title="Company">
          <SettingsRow label="Company name" htmlFor="settings-name">
            <Control>
              <Input
                id="settings-name"
                className="h-8"
                value={form.name}
                required
                aria-invalid={!form.name.trim() || undefined}
                onChange={(e) => set("name", e.target.value)}
              />
              {!form.name.trim() ? <FieldError>The company needs a name.</FieldError> : null}
            </Control>
          </SettingsRow>
          <SettingsRow
            label="Reporting currency"
            hint="Shown on every Friday report."
            htmlFor="settings-currency"
          >
            <Control>
              <NativeSelect
                id="settings-currency"
                size="sm"
                className="w-full"
                value={form.currency}
                onChange={(e) => set("currency", e.target.value)}
              >
                {[...new Set([form.currency, ...CURRENCIES])].map((c) => (
                  <NativeSelectOption key={c} value={c}>
                    {c}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Control>
          </SettingsRow>
          <SettingsRow label="Time zone" htmlFor="settings-tz">
            <Control>
              <Input
                id="settings-tz"
                className="h-8"
                list="settings-tz-list"
                value={form.timezone}
                aria-invalid={badZone || undefined}
                onChange={(e) => set("timezone", e.target.value)}
              />
              <datalist id="settings-tz-list">
                {TIME_ZONES.map((z) => (
                  <option key={z} value={z} />
                ))}
              </datalist>
              {badZone ? (
                <FieldError>Use an IANA time zone, like America/New_York.</FieldError>
              ) : null}
            </Control>
          </SettingsRow>
          <SettingsRow label="Week starts on" htmlFor="settings-week-start">
            <Control>
              <NativeSelect
                id="settings-week-start"
                size="sm"
                className="w-full"
                value={String(form.weekStartDay)}
                onChange={(e) => set("weekStartDay", Number(e.target.value))}
              >
                {WEEKDAYS.map((d, i) => (
                  <NativeSelectOption key={d} value={String(i + 1)}>
                    {d}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Control>
          </SettingsRow>
          <SettingsRow
            label="DE names"
            hint="Person name: a DE may get a name shown before its role."
            htmlFor="settings-de-names"
          >
            <Control>
              <NativeSelect
                id="settings-de-names"
                size="sm"
                className="w-full"
                value={form.deNames}
                onChange={(e) => set("deNames", e.target.value === "person" ? "person" : "role")}
              >
                <NativeSelectOption value="role">Role (Marketing Director)</NativeSelectOption>
                <NativeSelectOption value="person">
                  Person name (Maya · Marketing Director)
                </NativeSelectOption>
              </NativeSelect>
            </Control>
          </SettingsRow>
          <SettingsRow label="Fiscal year starts in" htmlFor="settings-fiscal">
            <Control>
              <NativeSelect
                id="settings-fiscal"
                size="sm"
                className="w-full"
                value={String(form.fiscalMonth)}
                onChange={(e) => set("fiscalMonth", Number(e.target.value))}
              >
                {MONTHS.map((m, i) => (
                  <NativeSelectOption key={m} value={String(i + 1)}>
                    {m}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Control>
          </SettingsRow>
        </SettingsGroup>

        <SettingsGroup id="friday-report" title="Friday report">
          <SettingsRow label="Generate at" hint="Company time." htmlFor="settings-generate-at">
            <Control>
              <Input
                id="settings-generate-at"
                type="time"
                className="h-8"
                value={form.generateAt}
                onChange={(e) => set("generateAt", e.target.value)}
              />
            </Control>
          </SettingsRow>
          <SettingsRow
            label="Send automatically two hours later"
            hint="Otherwise a manager sends it from the report page."
            htmlFor="settings-auto-send"
          >
            <Switch
              id="settings-auto-send"
              checked={form.autoSend}
              onCheckedChange={(v) => set("autoSend", v)}
            />
          </SettingsRow>
          <SettingsRow
            wide
            label="Extra recipients"
            hint="One email per line, besides the owners, admins and client viewers."
            htmlFor="settings-recipients"
          >
            <div className="flex w-full flex-col gap-1">
              <Textarea
                id="settings-recipients"
                rows={3}
                value={form.recipients}
                aria-invalid={badRecipient ? true : undefined}
                onChange={(e) => set("recipients", e.target.value)}
              />
              {badRecipient ? (
                <FieldError>{`"${badRecipient}" is not an email address.`}</FieldError>
              ) : null}
            </div>
          </SettingsRow>
          <div data-slot="report-recipients" className="border-t border-subtle">
            <p className="px-4 pt-2.5 text-xs font-medium text-muted-foreground">
              Owners, admins and client viewers who get it
            </p>
            <PeopleRows
              q={people}
              empty="No owners, admins or client viewers yet."
              rows={reportPeople}
              render={(p) => (
                <SettingsRow
                  key={p.id}
                  data-member={p.id}
                  label={p.display_name}
                  hint={p.email ?? undefined}
                  htmlFor={`report-${p.id}`}
                >
                  <Switch
                    id={`report-${p.id}`}
                    checked={!form.optedOut.includes(p.id)}
                    onCheckedChange={(on) =>
                      set(
                        "optedOut",
                        on ? form.optedOut.filter((id) => id !== p.id) : [...form.optedOut, p.id],
                      )
                    }
                  />
                </SettingsRow>
              )}
            />
          </div>
        </SettingsGroup>

        <SettingsGroup id="cost-rates" title={`Cost rates (${form.currency} per hour)`}>
          <PeopleRows
            q={people}
            empty="No people yet."
            rows={persons.filter((p) => p.status !== "deactivated")}
            render={(p) => {
              const id = `rate-${p.id}`;
              const bad = badRate === p.id;
              return (
                <SettingsRow key={p.id} label={p.display_name} htmlFor={id}>
                  <Input
                    id={id}
                    inputMode="decimal"
                    className="h-8 w-36 text-right tabular-nums max-md:w-full"
                    placeholder="No rate"
                    value={form.rates[p.id] ?? ""}
                    aria-invalid={bad || undefined}
                    aria-label={`Hourly rate of ${p.display_name} (${form.currency})`}
                    onChange={(e) => set("rates", { ...form.rates, [p.id]: e.target.value })}
                  />
                </SettingsRow>
              );
            }}
          />
          {badRate ? (
            <div className="border-t border-subtle px-4 py-2">
              <FieldError>Rates are amounts of zero or more.</FieldError>
            </div>
          ) : null}
        </SettingsGroup>
        {operating ? (
          <SettingsGroup
            id="founder-sharing"
            data-slot="founder-sharing"
            title={
              <span className="inline-flex items-center gap-1">
                Sharing with Founder OS
                <InfoTip label="About sharing with Founder OS">
                  What a founder's portfolio receives from this company. Numbers and titles only;
                  never pages, tasks, chat or memory.
                  {view && !view.held ? " Not in any Founder OS yet." : ""}
                  {view && form.sharing === base.sharing && SHARING_SOURCE[view.source]
                    ? ` ${SHARING_SOURCE[view.source]}`
                    : ""}
                </InfoTip>
              </span>
            }
          >
            {share ? (
              <>
                <SettingsRow
                  label="KPIs"
                  hint={owner ? undefined : "Only an owner changes the sharing."}
                  htmlFor="settings-share-kpi"
                >
                  <Control>
                    <NativeSelect
                      id="settings-share-kpi"
                      value={share.kpi}
                      disabled={!owner}
                      onChange={(e) =>
                        set("sharing", {
                          ...share,
                          kpi: e.target.value as FounderSharing["kpi"],
                        })
                      }
                    >
                      <NativeSelectOption value="metrics">Metrics</NativeSelectOption>
                      <NativeSelectOption value="scores">Scores only</NativeSelectOption>
                      <NativeSelectOption value="off">Off</NativeSelectOption>
                    </NativeSelect>
                  </Control>
                </SettingsRow>
                <SettingsRow label="Finance" htmlFor="settings-share-finance">
                  <Control>
                    <NativeSelect
                      id="settings-share-finance"
                      value={share.finance}
                      disabled={!owner}
                      onChange={(e) =>
                        set("sharing", {
                          ...share,
                          finance: e.target.value as FounderSharing["finance"],
                        })
                      }
                    >
                      <NativeSelectOption value="totals">Totals</NativeSelectOption>
                      <NativeSelectOption value="off">Off</NativeSelectOption>
                    </NativeSelect>
                  </Control>
                </SettingsRow>
                {SHARING_TOGGLES.map(([k, label]) => (
                  <SettingsRow key={k} label={label} htmlFor={`settings-share-${k}`}>
                    <Switch
                      id={`settings-share-${k}`}
                      checked={share[k]}
                      disabled={!owner}
                      onCheckedChange={(v) => set("sharing", { ...share, [k]: v })}
                    />
                  </SettingsRow>
                ))}
              </>
            ) : (
              <p className="px-4 py-3 text-[13px] text-muted-foreground">
                The sharing could not be read. Reload to try again.
              </p>
            )}
          </SettingsGroup>
        ) : null}
        <SettingsGroup id="security" title="Security">
          <SettingsRow
            label="Require two-step sign-in for owners and admins"
            hint={
              !owner
                ? "Only an owner changes this."
                : "Owners and admins who have not set it up are held at a set-up screen until they do. Agency operators always use it."
            }
            htmlFor="settings-require-mfa"
          >
            <Switch
              id="settings-require-mfa"
              checked={form.requireMfa}
              disabled={!owner || (!me.mfa.enrolled && !form.requireMfa)}
              onCheckedChange={(v) => set("requireMfa", v)}
            />
          </SettingsRow>
          {owner && !me.mfa.enrolled && !form.requireMfa ? (
            <div className="border-t border-subtle px-4 py-2.5 text-xs text-muted-foreground">
              Set up two-step sign-in for yourself first, in{" "}
              <Link to="/account" hash="two-step" className="text-secondary underline">
                Account &amp; security
              </Link>
              .
            </div>
          ) : null}
        </SettingsGroup>
        <div className="flex justify-end">
          <SaveIndicator savedAt={savedAt} />
        </div>
      </form>
      <SaveDock
        dirty={dirty}
        saving={save.isPending}
        saveLabel="Save settings"
        message={invalid ? "Fix the marked fields to save" : "Unsaved changes"}
        onSave={onSave}
        onDiscard={() => setForm(base)}
      />
    </>
  );
}

/** The right-hand control column of a row: the field and its error under it. */
function Control({ children }: { children: ReactNode }) {
  return <div className="flex w-56 flex-col gap-1 max-md:w-full">{children}</div>;
}

function PeopleRows({
  q,
  rows,
  empty,
  render,
}: {
  q: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => unknown;
  };
  rows: Person[];
  empty: string;
  render: (p: Person) => ReactNode;
}) {
  if (q.isPending)
    return (
      <div className="p-4">
        <Loading label="Loading people" rows={2} />
      </div>
    );
  if (q.isError)
    return (
      <div className="p-4">
        <LoadError what="People" error={q.error} />
      </div>
    );
  return (
    <div className="flex flex-col">
      {rows.length ? (
        rows.map(render)
      ) : (
        <p className="px-4 py-3 text-[13px] text-muted-foreground">{empty}</p>
      )}
      <div className="border-t border-subtle px-4 py-2 empty:hidden">
        <LoadMore
          what="people"
          hasMore={q.hasNextPage}
          loading={q.isFetchingNextPage}
          onMore={() => void q.fetchNextPage()}
        />
      </div>
    </div>
  );
}
